/**
 * TanStack Query Client Configuration
 *
 * Enhanced QueryClient configuration with:
 * - Default options for queries and mutations
 * - Retry logic with exponential backoff
 * - Cache management (staleTime, gcTime)
 * - Network mode configuration
 * - Query cancellation support
 * - Development tools integration (see App.tsx)
 *
 * Note: Global error handling is done at the hook level or via ErrorBoundary
 */
import {MutationCache, QueryCache, QueryClient} from '@tanstack/react-query';
import {logger} from '@/shared/lib/logger/logger';

/**
 * Default stale time: 5 minutes
 * Data is considered fresh for 5 minutes, reducing unnecessary refetches
 */
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Default garbage collection time: 10 minutes
 * Unused data is kept in cache for 10 minutes (was cacheTime in v4)
 */
const DEFAULT_GC_TIME = 10 * 60 * 1000; // 10 minutes

/**
 * Retry configuration
 * Retry failed requests 3 times with exponential backoff
 */
const RETRY_CONFIG = {
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
};

/**
 * Query cache with global error handling
 */
const queryCache = new QueryCache({
  onError: (error, query) => {
    // Log query errors for monitoring
    const errorObj = error instanceof Error ? error : new Error(String(error));
    logger.error('Query error', errorObj, {
      service: 'tanstack-query',
      queryKey: query.queryKey,
      type: 'query_error',
    });
  },
});

/**
 * Mutation cache with global error handling
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    // Log mutation errors for monitoring
    const errorObj = error instanceof Error ? error : new Error(String(error));
    logger.error('Mutation error', errorObj, {
      service: 'tanstack-query',
      mutationKey: mutation.options.mutationKey,
      type: 'mutation_error',
    });
  },
});

/**
 * Create and configure QueryClient instance with enhanced features
 */
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_GC_TIME,
      refetchOnWindowFocus: false, // Don't refetch on window focus (better UX)
      refetchOnMount: true, // Refetch when component mounts (fresh data)
      refetchOnReconnect: true, // Refetch when network reconnects
      networkMode: 'online', // Only run queries when online
      ...RETRY_CONFIG,
      // Enable structural sharing for better performance
      structuralSharing: true,
    },
    mutations: {
      ...RETRY_CONFIG,
      networkMode: 'online', // Only run mutations when online
      // Retry mutations on network errors (but not on 4xx errors)
      retry: (failureCount, error) => {
        // Don't retry on client errors (4xx)
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status?: number }).status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for network errors
        return failureCount < 3;
      },
    },
  },
});

/**
 * Export queryClient for use in components and hooks
 */
export default queryClient;
